import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import { AuthenticationStatus, OAuthSignInMethod } from '../bitski';
import { AuthenticationError } from '../errors/authentication-error';
import { OAuthManager } from './oauth-manager';
import { TokenStore } from './token-store';
import { User } from './user';
import { UserStore } from './user-store';
export var OpenidAuthProvider = /*#__PURE__*/function () {
  function OpenidAuthProvider(clientId, redirectUri, additionalScopes, opts) {
    _classCallCheck(this, OpenidAuthProvider);

    opts = opts || {};
    var settings = {
      additionalScopes: additionalScopes,
      clientId: clientId,
      redirectUri: redirectUri
    };
    Object.assign(settings, opts);
    this.oauthManager = new OAuthManager(settings);
    this.tokenStore = new TokenStore(clientId, opts.store);
    this.userStore = new UserStore(clientId, opts.store);
  }

  _createClass(OpenidAuthProvider, [{
    key: "getAccessToken",
    value: function getAccessToken() {
      if (this.tokenStore.currentToken) {
        return Promise.resolve(this.tokenStore.currentToken);
      }

      if (this.tokenStore.refreshToken) {
        return this.refreshAccessToken();
      }

      return Promise.reject(AuthenticationError.NotSignedIn());
    }
  }, {
    key: "getRefreshToken",
    value: function getRefreshToken() {
      if (this.tokenStore.refreshToken) {
        return Promise.resolve(this.tokenStore.refreshToken);
      } // Error: the user did not approve this app for offline access


      if (this.tokenStore.currentToken) {
        return Promise.reject(AuthenticationError.NoRefreshToken());
      } // Error: the user is not signed in.


      return Promise.reject(AuthenticationError.NotSignedIn());
    }
  }, {
    key: "invalidateToken",
    value: function invalidateToken() {
      if (this.tokenStore.currentToken) {
        this.tokenStore.invalidateCurrentToken();
      }

      if (this.signOutCallback) {
        this.signOutCallback();
      }

      return Promise.resolve();
    }
  }, {
    key: "refreshAccessToken",
    value: function refreshAccessToken() {
      var _this = this;

      if (this.tokenStore.refreshToken) {
        return this.oauthManager.refreshAccessToken(this.tokenStore.refreshToken).then(function (tokenResponse) {
          _this.tokenStore.persistTokenResponse(tokenResponse);

          return tokenResponse.accessToken;
        }).catch(function (error) {
          // If we can't renew, we likely have bad data
          _this.tokenStore.clear();

          _this.userStore.clear();

          throw error;
        });
      }

      return Promise.reject(AuthenticationError.NoRefreshToken());
    }
  }, {
    key: "signIn",
    value: function signIn(method, opts) {
      var _this2 = this;

      var promise;

      switch (method) {
        case OAuthSignInMethod.Redirect:
          promise = this.oauthManager.signInRedirect(opts);
          break;

        case OAuthSignInMethod.Silent:
          return Promise.reject(AuthenticationError.UnsupportedAuthenticationMethod());

        default:
          promise = this.oauthManager.signInPopup(opts);
          break;
      }

      return promise.then(function (tokenResponse) {
        _this2.tokenStore.persistTokenResponse(tokenResponse);

        return _this2.loadUser();
      });
    }
  }, {
    key: "connect",
    value: function connect() {
      var _this3 = this;

      return this.refreshAccessToken().then(function () {
        return _this3.loadUser();
      });
    }
  }, {
    key: "getUser",
    value: function getUser() {
      return this.getOrFetchUser();
    }
  }, {
    key: "signInOrConnect",
    value: function signInOrConnect() {
      var signInMethod = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : OAuthSignInMethod.Popup;
      var opts = arguments.length > 1 ? arguments[1] : undefined;

      switch (this.authStatus) {
        case AuthenticationStatus.Connected:
          return this.loadUser();

        case AuthenticationStatus.Expired:
          return this.connect();

        case AuthenticationStatus.NotConnected:
          return this.signIn(signInMethod, opts);
      }
    }
  }, {
    key: "redirectCallback",
    value: function redirectCallback() {
      var _this4 = this;

      return this.oauthManager.redirectCallback().then(function (tokenResponse) {
        _this4.tokenStore.persistTokenResponse(tokenResponse);

        return _this4.loadUser();
      });
    }
  }, {
    key: "signOut",
    value: function signOut() {
      this.tokenStore.clear();
      this.userStore.clear(); // Call the sign out callback if one has been provided

      if (this.signOutCallback) {
        this.signOutCallback();
      } // We don't currently have the ability to invalidate access tokens, so for now simply resolve.
      // Down the road this may perform a network request to invalidate.


      return Promise.resolve();
    }
  }, {
    key: "getOrFetchUser",
    value: function getOrFetchUser() {
      var currentUser = this.userStore.currentUser;

      if (currentUser) {
        return Promise.resolve(currentUser);
      }

      return this.loadUser();
    }
  }, {
    key: "loadUser",
    value: function loadUser() {
      var _this5 = this;

      return this.getAccessToken().then(function (accessToken) {
        return _this5.oauthManager.requestUserInfo(accessToken);
      }).then(function (json) {
        return User.fromJson(json);
      }).then(function (user) {
        _this5.userStore.set(user);

        return user;
      });
    }
  }, {
    key: "authStatus",
    get: function get() {
      if (this.tokenStore.currentToken) {
        return AuthenticationStatus.Connected;
      } else if (this.tokenStore.refreshToken) {
        return AuthenticationStatus.Expired;
      } else {
        return AuthenticationStatus.NotConnected;
      }
    }
  }]);

  return OpenidAuthProvider;
}();